import { useMediaQuery, useTheme } from '@material-ui/core';
import { connect, FormikContextType, getIn } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ImageType } from '~/Models/DTO/Location.models';
import { ExtendedLocation } from '../../ExtendedLocation.model';
import _ from 'lodash';
import { CORE_SERVICE_URLS } from '~/Constants/Urls.constants';
import { useRequest, useUUID } from '~/Hooks';
import { IUploadedImage } from '~/Models/DTO/UploadedImage.mode';
import { Error } from '~/Components';
import { ImageUploader } from './ImageUploader';

export interface IImageResponse {
  url: string;
}

export const LocationEditCustomImageUploader = connect<{}, ExtendedLocation>(
  ({ formik }: { formik: FormikContextType<ExtendedLocation> }) => {
    if (!formik) {
      return null;
    }
    const images: ImageType[] = getIn(formik.values, 'images', []);
    const errors = getIn(formik.errors, 'images');
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { t } = useTranslation();
    const { request } = useRequest();
    const { createUUID } = useUUID();

    const handleDragDrop = (fromOrder: number, toOrder: number) => {
      const tempImages = _.sortBy([...images], ['order']);
      const fromImageIndex = tempImages.findIndex((image) => image.order === fromOrder);
      const toImageIndex = tempImages.findIndex((image) => image.order === toOrder);
      if (toImageIndex >= tempImages.length) {
        let length: number = toImageIndex - tempImages.length + 1;
        while (length--) {
          tempImages.push();
        }
      }
      tempImages.splice(toImageIndex, 0, tempImages.splice(fromImageIndex, 1)[0]);
      tempImages.forEach((image, index) => {
        image.order = index + 1;
      });
      formik.setFieldValue('images', tempImages);
    };

    const handleEditImage = (imageSrc: string, imageOrder: number) => {
      const tempImages = images.filter((img) => img.order !== imageOrder);
      tempImages.push({
        imageUrl: imageSrc,
        imageOriginalUrl: imageSrc,
        order: imageOrder,
      });
      formik.setFieldValue('images', tempImages);
    };

    const handleNewImage = async (imageData: string) => {
      const response = await request<IUploadedImage>({
        url: CORE_SERVICE_URLS.UPLOAD_IMAGE,
        method: 'POST',
        data: {
          name: createUUID(),
          imageData,
        },
      });
      if (response.status >= 200 && response.status < 300) {
        const tempImages = _.sortBy([...images], ['order']);
        if (response.data?.url && tempImages) {
          tempImages.push({
            imageOriginalUrl: response.data.imageOriginalUrl,
            imageUrl: response.data.url,
            order: (images.length || 0) + 1,
          });
          formik.setFieldValue('images', tempImages);
        }
        formik.setErrors({
          images: undefined,
        });
      } else if (response.status === 413) {
        formik.setErrors({
          images: t('formik.error.imageSize'),
        });
      } else {
        formik.setErrors({
          images: t('app.default.error'),
        });
      }
    };

    const handleDeleteImage = (imageIndex: number) => {
      const tempImages = _.sortBy([...images], ['order']);
      tempImages?.splice(imageIndex, 1);
      const reOrderImages: ImageType[] = [];
      _.sortBy(tempImages, ['order']).map((image, index) =>
        reOrderImages.push({
          ...image,
          order: index + 1,
        }),
      );
      formik.setFieldValue('images', tempImages);
    };

    return (
      <>
        <Error error={{ error: errors }} />
        <ImageUploader
          onEditImage={handleEditImage}
          onDeleteImage={handleDeleteImage}
          onNewImage={handleNewImage}
          onDragDrop={handleDragDrop}
          title={t('form.field.location.addImages')}
          description={t('form.field.location.addImages.description')}
          images={images}
          imageStyle={{ width: 140, height: 140 }}
          canvasStyle={{
            width: isTablet ? 320 : 640,
            height: isTablet ? 180 : 360,
            color: [0, 0, 0, 0.4],
            borderRadius: 0,
            border: 0,
          }}
          tooltip={{
            title: t('form.field.location.addImage.tooltip'),
            placement: isMobile ? 'bottom' : 'auto-start',
          }}
        />
      </>
    );
  },
);
